import type { HttpErrorResponse } from '@angular/common/http';

import { createAction, createSelector } from '@ngrx/store';

import { UserSelectors } from '@core/account-data';
import { CandidateSelectors } from '@core/candidate-data';
import type { CorrelationId, LoadedPaginationState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  objectToScope,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  SharedContainersLoadParams,
  SharingContainerApi,
  SharingContainerSaveData,
} from '@core/sharing-container-types';

const idSelector = (container: { id: number }) => container.id;
const { actions, namespace, selectors, reducer } =
  createEntityCollectionStore<SharingContainerApi>()('sharing-containers', {
    references: makeRef => [
      makeRef('adminId', 'admin', UserSelectors.byId),
      makeRef('candidateIds', 'candidates', ids => CandidateSelectors.byIds(ids || [])),
    ],
    idSelector,

    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchCollection: (state, payload: { params: SharedContainersLoadParams; scope: string }) =>
          defaultActions.fetchCollection(state, payload),
        fetchCollectionSuccess: (
          state,
          payload: { data: SharingContainerApi[]; scope: string; meta: LoadedPaginationState },
        ) =>
          pipeState(
            state,
            next => defaultActions.fetchCollectionSuccess(next, payload),
            next => adapter.setPaginationMeta(next, payload.meta, { scope: payload.scope }),
          ),

        fetchCandidateCollection: (state, payload: { scope: string; candidateId: number }) =>
          defaultActions.fetchCollection(state, payload),
        fetchCandidateCollectionSuccess: defaultActions.fetchCollectionSuccess,
        fetchCandidateCollectionFailed: defaultActions.fetchCollectionFailed,

        save: (state, payload: { container: SharingContainerSaveData } & CorrelationId) => {
          const id = payload.container.id || payload.correlationId;
          return id ? adapter.markAsPending(state, { scope: null, entities: [id] }) : state;
        },
        saveSuccess: (state, payload: { container: SharingContainerApi } & CorrelationId) =>
          pipeState(
            state,
            next => adapter.writeOne(next, payload.container),
            next => adapter.markAsLoaded(next, { scope: null, entities: [payload.container.id] }),
            next =>
              payload.correlationId
                ? adapter.setAlias(next, { [payload.correlationId]: payload.container.id })
                : next,
          ),
        saveFailed: (
          state,
          payload: { error: HttpErrorResponse; data: SharingContainerSaveData } & CorrelationId,
        ) => {
          const id = payload.data.id || payload.correlationId;
          return id
            ? adapter.setError(state, payload.error, { scope: null, entities: [id] })
            : state;
        },
        bulkDeleteContainers: (state, payload: { ids: number[]; scope: string }) =>
          adapter.markAsPending(state),

        bulkDeleteContainersSuccess: (
          state,
          payload: { data: { uuid: string; ids: number[] }; scope: string },
        ) =>
          pipeState(adapter.removeMany(state, payload.data.ids), next =>
            adapter.markAsLoaded(next),
          ),

        bulkDeleteContainersFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload),
        sendShareLinks: ngrxEvent<{ data: { id: number; emails: string[] } } & CorrelationId>(),
        sendShareLinksSuccess: ngrxEvent<
          {
            data: { container: SharingContainerApi; emailsSentCounter: number };
          } & CorrelationId
        >(),
        sendShareLinksFailed: ngrxEvent<
          {
            error: HttpErrorResponse;
            data: { id: number; emails: string[] };
          } & CorrelationId
        >(),
      };
    },
  });

type UsedActions = Pick<
  typeof actions,
  | 'save'
  | 'saveSuccess'
  | 'saveFailed'
  | 'removeOnePessimistic'
  | 'removeOnePessimisticSuccess'
  | 'removeOnePessimisticFailed'
  | 'sendShareLinks'
  | 'sendShareLinksSuccess'
  | 'sendShareLinksFailed'
  | 'fetchCandidateCollection'
  | 'fetchCandidateCollectionSuccess'
  | 'fetchCandidateCollectionFailed'
  | 'fetchCollection'
  | 'fetchCollectionSuccess'
  | 'fetchCollectionFailed'
  | 'bulkDeleteContainers'
  | 'bulkDeleteContainersSuccess'
  | 'bulkDeleteContainersFailed'
  | 'fetchOne'
  | 'fetchOneSuccess'
  | 'fetchOneFailed'
>;

export const getCandidateScope = (candidateId: number) => `candidate:${candidateId}`;
const SharingContainerActions = {
  ...(<UsedActions>actions),
  fetchCandidateCollection: createAction(
    actions.fetchCandidateCollection.type,
    ({ payload: { candidateId } }: { payload: { candidateId: number } }) => {
      const { payload } = actions.fetchCandidateCollection({
        payload: { candidateId, scope: getCandidateScope(candidateId) },
      });
      return { payload };
    },
  ),
};

const selectCandidateEntities = memoizeSelector((candidateId: number) =>
  selectors.entitiesWithReferences({ scope: getCandidateScope(candidateId) }),
);
export const getSharingContainerPaginationScope = (
  scope: 'settings' | 'dropdown',
  params: SharedContainersLoadParams,
) => `${scope}:${objectToScope(params)}`;
const SharingContainerSelectors = {
  ...selectors,
  candidateEntities: selectCandidateEntities,
  candidateEntitiesActive: memoizeSelector(
    ({ candidateId, scope }: { candidateId: number; scope?: string }) =>
      createSelector(
        selectors.entitiesWithReferences({ scope: scope ? scope : getCandidateScope(candidateId) }),
        containers =>
          containers.filter(container =>
            scope?.length
              ? container.active && !!container.candidateIds?.includes(candidateId)
              : container.active,
          ),
      ),
  ),
};

export {
  SharingContainerActions,
  SharingContainerSelectors,
  namespace as SharingContainerNamespace,
  reducer as SharingContainerReducer,
};
